// src/pages/profile/MyProfile.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import AddressFields from '../../components/common/address/AddressFields';
import { hasAnyAddress, validateAddressIfPresent } from '../../components/common/address/addressValidators';
import { DatePicker } from '../../components/common/DatePicker';
import { formatDate, parseRegionalDate, toISODateFromRegional, academyTodayDate, todayIso } from '../../utils/date';
import Button from '../../components/common/Button';
import PhoneNumberFields from '../../components/common/PhoneNumberFields';
import { validateStoredPhone } from '../../utils/phone';
import ListPageLayout, { ListPageHeader, ListLoadingSpinner } from '../../components/layout/ListPageLayout';
import GuardiansSection from '../../components/guardians/GuardiansSection';
import { getActiveRole } from '../../utils/auth';
import { TextInput } from '../../components/common/form';
import { isValidPersonName } from '../../utils/name';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import { apiErrorMessage, safeUserMessage } from '../../utils/apiError';
import { useRegionalSettings } from '../../context/RegionalSettingsContext';
import { applyUiLanguage, normalizeUiLanguage } from '../../utils/uiLanguage';
// --- end helpers ---



const MyProfile = () => {
    const { t, i18n } = useTranslation();
    const { settings: regionalSettings } = useRegionalSettings();
    const enabledUiLanguages = Array.isArray(regionalSettings?.enabledUiLanguages)
        && regionalSettings.enabledUiLanguages.length
        ? regionalSettings.enabledUiLanguages
        : ['en'];
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [userId, setUserId] = useState(null);
    const activeRole = getActiveRole();

    // --- password change state ---
    const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [pwSaving, setPwSaving] = useState(false);
    const [pwError, setPwError] = useState('');
    const [pwSuccess, setPwSuccess] = useState('');
    const [dsarSending, setDsarSending] = useState(false);
    const [dsarMsg, setDsarMsg] = useState('');
    const [dsarError, setDsarError] = useState(false);

    // unified form state (align with Manager/Coach forms + AddressFields)
    const [form, setForm] = useState({
        firstName: '',
        middleName: '',
        lastName: '',
        dob: '',
        gender: 'MALE',
        username: '',
        email: '',
        phoneNumber: '',
        preferredLanguage: normalizeUiLanguage(i18n.language || localStorage.getItem('uiLanguage') || 'en'),

        // address (optional)
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        latitude: '',
        longitude: '',
    });

    const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

    useEffect(() => {
        (async () => {
            try {
                const res = await API.get('/me');
                const u = res.data || {};
                setUserId(u.id ?? u.userId ?? null);
                const a = u.address || {};
                const preferred = normalizeUiLanguage(
                    u.preferredLanguage || i18n.language || localStorage.getItem('uiLanguage') || 'en'
                );
                if (u.preferredLanguage) {
                    applyUiLanguage(u.preferredLanguage);
                }
                setForm((prev) => ({
                    ...prev,
                    firstName: u.firstName || '',
                    middleName: u.middleName || '',
                    lastName: u.lastName || '',
                    dob: u.dob || '',
                    gender: u.gender || 'MALE',
                    username: u.username || '',
                    email: u.email || '',
                    phoneNumber: u.phoneNumber || u.phone_number || '',
                    preferredLanguage: preferred,
                    addressLine1: a.addressLine1 || '',
                    addressLine2: a.addressLine2 || '',
                    city: a.city || '',
                    state: a.state || '',
                    country: a.country || '',
                    pincode: a.pincode || '',
                    latitude: a.latitude != null ? String(a.latitude) : '',
                    longitude: a.longitude != null ? String(a.longitude) : '',
                }));
            } catch (e) {
                console.error('GET /me failed:', e);
                setLoadError(t('pages.profile.loadFailed'));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const Label = ({ text, required }) => (
        <label className="form-label">
            {text} {required && <span className="text-red-500">*</span>}
        </label>
    );

    const validate = () => {
        if (!form.firstName || !isValidPersonName(form.firstName)) {
            alert(t('pages.profile.firstNameRequired'));
            return false;
        }
        if (form.middleName && !isValidPersonName(form.middleName)) {
            alert(t('pages.profile.middleNameLetters'));
            return false;
        }
        if (!form.lastName || !isValidPersonName(form.lastName)) {
            alert(t('pages.profile.lastNameRequired'));
            return false;
        }
        if (form.dob) {
            const d = parseRegionalDate(form.dob);
            const today = academyTodayDate();
            if (isNaN(d.getTime()) || d >= today) {
                alert(t('pages.profile.invalidDob'));
                return false;
            }
        }
        const phoneCheck = validateStoredPhone(form.phoneNumber);
        if (!phoneCheck.valid) {
            alert(phoneCheck.message);
            return false;
        }

        // Address: optional; validate only if present using shared validator
        const addressObj = {
            addressLine1: form.addressLine1,
            addressLine2: form.addressLine2,
            city: form.city,
            state: form.state,
            country: form.country,
            pincode: form.pincode,
            latitude: form.latitude,
            longitude: form.longitude,
        };
        const v = validateAddressIfPresent(addressObj);
        if (v && v.ok === false) {
            alert(v.message || t('pages.profile.invalidAddress'));
            return false;
        }
        if (form.latitude && isNaN(parseFloat(form.latitude))) {
            alert(t('pages.profile.invalidLatitude'));
            return false;
        }
        if (form.longitude && isNaN(parseFloat(form.longitude))) {
            alert(t('pages.profile.invalidLongitude'));
            return false;
        }

        return true;
    };

    const persistProfile = async () => {
        if (!validate()) {
            throw new Error('validation');
        }
        const addressPresent = hasAnyAddress({
            addressLine1: form.addressLine1,
            addressLine2: form.addressLine2,
            city: form.city,
            state: form.state,
            country: form.country,
            pincode: form.pincode,
            latitude: form.latitude,
            longitude: form.longitude,
        });

        const preferredLanguage = normalizeUiLanguage(form.preferredLanguage);
        const payload = {
            firstName: form.firstName,
            middleName: form.middleName,
            lastName: form.lastName,
            dob: toISODateFromRegional(form.dob) || null,
            gender: form.gender,
            username: form.username,
            email: form.email,
            phoneNumber: validateStoredPhone(form.phoneNumber).normalized,
            preferredLanguage,
            ...(addressPresent && {
                address: {
                    addressLine1: form.addressLine1,
                    addressLine2: form.addressLine2,
                    city: form.city,
                    state: form.state,
                    country: form.country,
                    pincode: form.pincode,
                    latitude: form.latitude ? parseFloat(form.latitude) : null,
                    longitude: form.longitude ? parseFloat(form.longitude) : null,
                },
            }),
        };

        await API.put('/me', payload);
        applyUiLanguage(preferredLanguage);
    };

    const { markSaved } = useUnsavedChanges(loading ? null : form, {
        enabled: !loading && !loadError,
        onSave: persistProfile,
    });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSaving(true);
        try {
            await persistProfile();
            markSaved(form);
            alert(t('pages.profile.profileUpdated'));
        } catch (err) {
            console.error('Update profile failed', err);
            alert(t('pages.profile.profileUpdateFailed'));
        } finally {
            setSaving(false);
        }
    };

    const onChangePassword = async (e) => {
        e.preventDefault();
        setPwError('');
        setPwSuccess('');

        if (!pwForm.currentPassword) {
            setPwError(t('pages.profile.enterCurrentPassword'));
            return;
        }
        if (pwForm.newPassword.length < 8) {
            setPwError(t('pages.profile.passwordMinLength'));
            return;
        }
        if (pwForm.newPassword !== pwForm.confirmPassword) {
            setPwError(t('pages.profile.passwordsMismatch'));
            return;
        }
        if (pwForm.newPassword === pwForm.currentPassword) {
            setPwError(t('pages.profile.passwordSameAsCurrent'));
            return;
        }

        setPwSaving(true);
        try {
            await API.post('/me/change-password', {
                currentPassword: pwForm.currentPassword,
                newPassword: pwForm.newPassword,
                confirmPassword: pwForm.confirmPassword,
            });
            setPwSuccess(t('pages.profile.passwordChanged'));
            setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            const msg = apiErrorMessage(err, t('pages.profile.passwordChangeFailed'));
            setPwError(msg);
        } finally {
            setPwSaving(false);
        }
    };

    if (loading) {
        return (
            <ListPageLayout>
                <ListPageHeader title={t('pages.profile.title')} subtitle={t('common.loading')} showBack={false} />
                <ListLoadingSpinner />
            </ListPageLayout>
        );
    }
    if (loadError)
        return (
            <ListPageLayout>
                <ListPageHeader title={t('pages.profile.title')} subtitle={t('pages.profile.subtitle')} />
                <div className="banner-danger">{loadError}</div>
                <Button onClick={() => window.location.reload()} variant="primary">{t('pages.profile.retry')}</Button>
            </ListPageLayout>
        );

    return (
        <ListPageLayout className="max-w-4xl mx-auto">
            <ListPageHeader
                title={t('pages.profile.title')}
                subtitle={t('pages.profile.subtitle')}
            />

            <form onSubmit={onSubmit} className="section-card grid grid-cols-1 gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                        label={t('forms.common.firstName')}
                        value={form.firstName}
                        onChange={(e) => setField('firstName', e.target.value)}
                        required
                    />
                    <TextInput
                        label={t('forms.common.middleName')}
                        value={form.middleName}
                        onChange={(e) => setField('middleName', e.target.value)}
                    />
                    <TextInput
                        label={t('forms.common.lastName')}
                        value={form.lastName}
                        onChange={(e) => setField('lastName', e.target.value)}
                        required
                    />
                    <div>
                        <Label text={t('forms.common.dateOfBirth')} />
                        <DatePicker
                            value={formatDate(form.dob)}
                            onChange={(e) => setField('dob', e)}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <Label text={t('forms.common.gender')} />
                        <select
                            className="select opacity-60 cursor-not-allowed"
                            value={form.gender}
                            onChange={() => { }}
                            disabled
                        >
                            <option value="MALE">{t('forms.common.male')}</option>
                            <option value="FEMALE">{t('forms.common.female')}</option>
                            <option value="OTHER">{t('forms.common.other')}</option>
                        </select>
                    </div>

                    <div>
                        <Label text={t('common.username')} />
                        <input
                            className="input opacity-60 cursor-not-allowed"
                            value={form.username}
                            disabled
                        />
                    </div>

                    <div>
                        <Label text={t('common.email')} />
                        <input
                            type="email"
                            className="input opacity-60 cursor-not-allowed"
                            value={form.email}
                            disabled
                        />
                    </div>

                    <div>
                        <Label text={t('forms.common.phoneNumber')} required />
                        <PhoneNumberFields
                            value={form.phoneNumber}
                            onChange={(v) => setField('phoneNumber', v)}
                            required
                        />
                    </div>

                    <div>
                        <Label text={t('pages.profile.preferredLanguage')} />
                        {enabledUiLanguages.length === 1 ? (
                            <p className="text-sm text-slate-700">
                                {t(`pages.profile.lang_${enabledUiLanguages[0]}`)}
                                <span className="block text-xs text-gray-500 mt-1">
                                    {t('pages.profile.preferredLanguageSingleHint', {
                                        defaultValue: 'Only one language is enabled for this academy.',
                                    })}
                                </span>
                            </p>
                        ) : (
                            <>
                                <select
                                    className="select"
                                    value={form.preferredLanguage}
                                    onChange={(e) => setField('preferredLanguage', normalizeUiLanguage(e.target.value))}
                                    aria-label={t('pages.profile.preferredLanguage')}
                                >
                                    {enabledUiLanguages.map((code) => (
                                        <option key={code} value={code}>
                                            {t(`pages.profile.lang_${code}`)}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">{t('pages.profile.preferredLanguageHint')}</p>
                            </>
                        )}
                    </div>
                </div>

                {/* Address (optional) — title rendered by AddressFields */}
                <AddressFields
                    value={form}
                    applyDefaultCountry={false}
                    onChange={(patchOrName, maybeValue) => {
                        if (typeof patchOrName === 'string') {
                            setField(patchOrName, maybeValue);
                        } else if (patchOrName && typeof patchOrName === 'object') {
                            setForm((prev) => ({ ...prev, ...patchOrName }));
                        }
                    }}
                />

                <div className="flex justify-end pt-2">
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={saving}
                    >
                        {saving ? t('pages.profile.saving') : t('pages.profile.saveChanges')}
                    </Button>
                </div>
            </form>

            {activeRole === 'STUDENT' && userId && (
                <GuardiansSection studentId={userId} />
            )}

            {(activeRole === 'STUDENT' || activeRole === 'PARENT') && (
                <div className="section-card max-w-4xl mt-6">
                    <h2 className="form-section-title mb-2">{t('pages.profile.requestDataTitle')}</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        {t('pages.profile.requestDataBody')}
                    </p>
                    {dsarMsg && (
                        <p className={`text-sm mb-3 ${dsarError ? 'text-red-600' : 'text-green-600'}`}>{dsarMsg}</p>
                    )}
                    <div className="flex flex-wrap gap-3">
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={dsarSending}
                            onClick={async () => {
                                setDsarMsg('');
                                setDsarError(false);
                                setDsarSending(true);
                                try {
                                    const res = await API.post('/me/request-data');
                                    setDsarMsg(safeUserMessage(res?.data?.message, t('pages.profile.requestSent')));
                                } catch (e) {
                                    setDsarError(true);
                                    setDsarMsg(apiErrorMessage(e, t('pages.profile.requestFailed')));
                                } finally {
                                    setDsarSending(false);
                                }
                            }}
                        >
                            {dsarSending ? t('pages.profile.sending') : t('pages.profile.requestData')}
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={async () => {
                                setDsarMsg('');
                                setDsarError(false);
                                try {
                                    const res = await API.get('/me/data-export');
                                    const blob = new Blob([JSON.stringify(res.data, null, 2)], {
                                        type: 'application/json',
                                    });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `my-data-export-${todayIso()}.json`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                    setDsarMsg(t('pages.profile.downloadDataDone'));
                                } catch (e) {
                                    setDsarError(true);
                                    setDsarMsg(apiErrorMessage(e, t('pages.profile.downloadDataFailed')));
                                }
                            }}
                        >
                            {t('pages.profile.downloadData')}
                        </Button>
                    </div>
                </div>
            )}

            <div className="section-card max-w-4xl mt-6">
                <h2 className="form-section-title mb-4">{t('pages.profile.changePassword')}</h2>
                <form onSubmit={onChangePassword} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                        label={t('pages.profile.currentPassword')}
                        type="password"
                        autoComplete="current-password"
                        value={pwForm.currentPassword}
                        onChange={(e) => setPwForm((p) => ({ ...p, currentPassword: e.target.value }))}
                        required
                    />
                    <div />
                    <TextInput
                        label={t('pages.profile.newPassword')}
                        type="password"
                        autoComplete="new-password"
                        value={pwForm.newPassword}
                        onChange={(e) => setPwForm((p) => ({ ...p, newPassword: e.target.value }))}
                        required
                        note={t('pages.profile.passwordMinHint')}
                    />
                    <TextInput
                        label={t('pages.profile.confirmPassword')}
                        type="password"
                        autoComplete="new-password"
                        value={pwForm.confirmPassword}
                        onChange={(e) => setPwForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                        required
                    />

                    {pwError && (
                        <div className="md:col-span-2">
                            <p className="text-red-600 text-sm">{pwError}</p>
                        </div>
                    )}
                    {pwSuccess && (
                        <div className="md:col-span-2">
                            <p className="text-green-600 text-sm">{pwSuccess}</p>
                        </div>
                    )}

                    <div className="md:col-span-2 flex justify-end">
                        <Button type="submit" variant="secondary" disabled={pwSaving}>
                            {pwSaving ? t('pages.profile.updating') : t('pages.profile.updatePassword')}
                        </Button>
                    </div>
                </form>
            </div>
        </ListPageLayout>
    );
};

export default MyProfile;